import { useBuyFlowContextOptional } from 'flows/tvi/generic-buyflow/contexts/BuyFlowContext';
import { useReservation } from '@updater/ui-widgets';
import { useUserData } from 'flows/core/hooks/use-profile';
import { Reservation } from 'flows/tvi/generic-buyflow/contexts/BuyFlowContext';

/**
 * Adapter hook that provides a unified interface for accessing reservation data
 * in both guest mode (from BuyFlowContext) and authenticated mode (from GraphQL).
 *
 * This hook abstracts the data source, allowing components to work seamlessly
 * in both modes without knowing which mode they're operating in.
 *
 * The GraphQL query is only triggered when there's no reservation in context,
 * avoiding unnecessary network requests in guest mode.
 *
 * @returns {ReservationAdapterResult} Reservation data and loading state
 *
 * @example
 * // Component works in both guest and authenticated modes
 * function MyComponent() {
 *   const { reservation, loading } = useReservationAdapter();
 *
 *   if (loading) return <Loading />;
 *   if (!reservation) return <NoReservation />;
 *
 *   return <div>{reservation.offer.name}</div>;
 * }
 */
export function useReservationAdapter(): ReservationAdapterResult {
  const context = useBuyFlowContextOptional();
  const { user } = useUserData();

  // Used for legacy buy flow fallback
  const reservation = useReservation({
    moveId: user?.currentMove?.id ?? '',
    skip: user?.currentMove?.id === undefined || Boolean(context?.reservation),
  });

  if (context?.reservation) {
    const providerCode = context.reservation.provider?.code;

    // Save provider code locally for TVI Formstore (client-side only)
    if (
      typeof window !== 'undefined' &&
      providerCode &&
      localStorage.getItem('tviCurrentProviderId') !== providerCode
    ) {
      localStorage.setItem('tviCurrentProviderId', providerCode);
    }

    return {
      reservation: context.reservation,
      loading: false,
    };
  }

  const { reservationInfo, offer, provider } = reservation;

  // Transform ui-widgets result to match our Reservation interface
  // ui-widgets returns: { loading, provider, offer, reservationInfo (with toAddress nested), refetch }
  // We need to compose it into Reservation shape: { reservationInfo, offer, provider }
  // Filter out extra fields like loading and refetch
  return {
    reservation: {
      reservationInfo,
      offer,
      provider,
    },
    loading: reservation.loading,
  };
}

/**
 * Result type matching useReservation hook signature for compatibility
 */
export type ReservationAdapterResult = {
  reservation: Reservation;
  loading?: boolean;
};
